/** Wire encode/decode. */

import { parseJson, type Result } from "./result";
import type { ClientMessage, ServerMessage } from "./presence";
import type { RosterClientMessage, RosterServerMessage } from "./roster";
import type { EncounterClientMessage, EncounterServerMessage } from "./encounter";
import type { GameClientMessage, GameServerMessage } from "./game";

/** Every message shape that can cross a socket in either direction — {@link decode}'s default `T`. */
type AnyWireMessage =
  | ClientMessage
  | ServerMessage
  | RosterClientMessage
  | RosterServerMessage
  | EncounterClientMessage
  | EncounterServerMessage
  | GameClientMessage
  | GameServerMessage;

// ── Helpers ──────────────────────────────────────────────────────────────────

export function encode(msg: AnyWireMessage): string {
  return JSON.stringify(msg);
}

/**
 * The **throwing** wire decode — a bare `JSON.parse` with a type assertion.
 *
 * Prefer {@link decodeMessage} in anything that reads a real socket. This one survives for
 * tests (which drive well-formed frames they just encoded, so a throw there is a genuine bug
 * worth surfacing) and for the one production site that deliberately wants a malformed frame
 * to be loud rather than dropped (`frontend/src/lib/game/useGameSession.ts`'s `onmessage`,
 * where an uncaught throw reaches the crash reporter).
 */
export function decode<T = AnyWireMessage>(raw: string): T {
  return JSON.parse(raw) as T;
}

/**
 * The wire decode on the `Result` channel — **use this in a ws `onMessage` handler**.
 *
 * A frame that isn't valid JSON is an *expected* rejection at a socket boundary (a truncated
 * send, a stray text frame, a client that isn't ours), and the whole repo answers it the same
 * way: drop the frame and keep the socket open. Nine handlers across `backend/` and `cli/`
 * hand-rolled that as a `try`/`catch` around `decode` whose catch arm was a bare `return`; they
 * now read
 *
 * ```ts
 * const decoded = decodeMessage<RosterServerMessage>(data);
 * if (!decoded.ok) return; // decode-or-drop
 * const msg = decoded.value;
 * ```
 *
 * which is the same behaviour with the failure in the signature. `parseJson` does the actual
 * catching, so this adds no new `try` anywhere (`CODE_STYLE.md`: wrap a throwing stdlib API
 * once, at the boundary, never at the call site).
 *
 * **The parse is all this validates.** `T` is an unchecked assertion, exactly as `decode`'s was —
 * a well-formed JSON frame carrying the wrong shape comes back `ok`. A handler that cares about
 * the shape still narrows on the value (`backend/src/world/safeNames.ts`'s `isSafeEncounterId` is
 * the worked example of why that matters).
 */
export function decodeMessage<T = AnyWireMessage>(raw: string): Result<T> {
  return parseJson<T>(raw);
}
